import { useEffect, useRef, useMemo, useState } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

const VIEWPORT_MARGIN_RATIO = 0.15;

function getExpandedBounds(bounds) {
    if (!bounds) return null;
    const latMargin = (bounds.north - bounds.south) * VIEWPORT_MARGIN_RATIO;
    const lngMargin = (bounds.east - bounds.west) * VIEWPORT_MARGIN_RATIO;
    return {
        north: bounds.north + latMargin,
        south: bounds.south - latMargin,
        east: bounds.east + lngMargin,
        west: bounds.west - lngMargin,
    };
}

function isInBounds(cluster, bounds) {
    if (!bounds) return true;
    const latitudeInBounds = cluster.lat >= bounds.south && cluster.lat <= bounds.north;
    const longitudeInBounds = bounds.west <= bounds.east
        ? cluster.lng >= bounds.west && cluster.lng <= bounds.east
        : cluster.lng >= bounds.west || cluster.lng <= bounds.east;
    return latitudeInBounds && longitudeInBounds;
}

/**
 * Lightweight marker clustering using AdvancedMarkerElement overlays.
 * Groups points into grid cells based on zoom and renders cluster icons
 * without relying on the legacy MarkerClusterer (which requires google.maps.Marker).
 *
 * Props:
 *  - clusters: Array of { key, lat, lng, count }
 *  - getStyle: (count, maxCount) => { url, width, height, textSize, fontWeight, TEXT_COLOR }
 *    restituisce lo stile (colore sfumato) del pallino in base alla numerosità,
 *    normalizzata sul massimo dei cluster attualmente visibili sulla mappa
 *  - onMaxCountChange: (maxCount) => void, notifica il massimo mostrato (per la legenda)
 */
export default function MarkersClusterOverlay({ clusters, getStyle, onMaxCountChange }) {
    const map = useGoogleMap();
    const overlaysRef = useRef(new Map());
    const [bounds, setBounds] = useState(null);

    // Tiene traccia dei confini visibili della mappa (aggiornati a fine pan/zoom)
    useEffect(() => {
        if (!map) return undefined;
        const update = () => {
            const nextBounds = map.getBounds()?.toJSON();
            if (!nextBounds) return;
            setBounds(previous => {
                if (
                    previous &&
                    previous.north === nextBounds.north &&
                    previous.south === nextBounds.south &&
                    previous.east === nextBounds.east &&
                    previous.west === nextBounds.west
                ) {
                    return previous;
                }
                return nextBounds;
            });
        };
        update();
        const listener = map.addListener('idle', update);
        return () => listener.remove();
    }, [map]);

    const visibleClusters = useMemo(() => {
        const expandedBounds = getExpandedBounds(bounds);
        return (clusters || []).filter(cluster => isInBounds(cluster, expandedBounds));
    }, [clusters, bounds]);

    // Massimo dei conteggi tra i cluster visibili nel viewport corrente.
    // Su questo valore si normalizza la scala cromatica (max visibile = rosso).
    const maxCount = useMemo(() => {
        return visibleClusters.reduce((maximum, cluster) => Math.max(maximum, cluster.count), 0);
    }, [visibleClusters]);

    useEffect(() => {
        if (onMaxCountChange) onMaxCountChange(maxCount);
    }, [maxCount, onMaxCountChange]);

    useEffect(() => {
        if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

        const visibleKeys = new Set(visibleClusters.map(cluster => cluster.key));

        overlaysRef.current.forEach((entry, key) => {
            if (!visibleKeys.has(key)) {
                entry.overlay.map = null;
                overlaysRef.current.delete(key);
            }
        });

        visibleClusters.forEach(cluster => {
            // Colore sfumato in base alla numerosità del cluster, normalizzato
            // sul massimo attualmente visibile sulla mappa
            const style = getStyle ? getStyle(cluster.count, maxCount) : null;
            if (!style) return;

            let entry = overlaysRef.current.get(cluster.key);
            if (!entry) {
                const container = document.createElement('div');
                container.style.position = 'relative';

                const img = document.createElement('img');
                img.style.width = '100%';
                img.style.height = '100%';
                container.appendChild(img);

                const label = document.createElement('div');
                label.style.position = 'absolute';
                label.style.inset = '0';
                label.style.display = 'flex';
                label.style.alignItems = 'center';
                label.style.justifyContent = 'center';
                container.appendChild(label);

                const overlay = new window.google.maps.marker.AdvancedMarkerElement({
                    map,
                    position: { lat: cluster.lat, lng: cluster.lng },
                    content: container,
                    zIndex: cluster.count,
                });
                entry = { overlay, container, img, label };
                overlaysRef.current.set(cluster.key, entry);
            } else {
                entry.overlay.position = { lat: cluster.lat, lng: cluster.lng };
                entry.overlay.zIndex = cluster.count;
            }

            entry.container.style.width = `${style.width}px`;
            entry.container.style.height = `${style.height}px`;
            if (entry.img.src !== style.url) entry.img.src = style.url;
            entry.label.textContent = cluster.count;
            entry.label.style.fontSize = `${style.textSize || 14}px`;
            entry.label.style.fontWeight = style.fontWeight || 'bold';
            entry.label.style.color = style.TEXT_COLOR || '#32323a';
        });
    }, [map, visibleClusters, getStyle, maxCount]);

    useEffect(() => () => {
        overlaysRef.current.forEach(entry => { entry.overlay.map = null; });
        overlaysRef.current.clear();
    }, [map]);

    return null;
}
